import React from 'react';
import { X, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { cn } from '../../utils/cn';
import { ProcessedWellbeingData } from '../../types/reports';
import { format, parseISO } from 'date-fns';
import { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { getMetricColor, getMetricScale } from '../../utils/wellbeingDataProcessor';

interface WellbeingMetricModalProps {
  metricKey: string;
  metricLabel: string;
  data: ProcessedWellbeingData;
  comparisonData?: ProcessedWellbeingData | null;
  isOpen: boolean;
  onClose: () => void;
}

interface ChartDataPoint {
  date: string;
  dateLabel: string;
  average: number;
  count: number;
}

const CustomTooltip = ({ active, payload }: any) => {
  if (!active || !payload || !payload.length) return null;

  const data = payload[0].payload;

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
      <p className="font-semibold text-gray-900 mb-2">
        {format(parseISO(data.date), 'MMM d, yyyy')}
      </p>
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-700">Average:</span>
        <span className="text-sm font-semibold text-gray-900 ml-3">
          {data.average.toFixed(1)}
        </span>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-500">Entries:</span>
        <span className="text-xs text-gray-600 ml-3">
          {data.count}
        </span>
      </div>
    </div>
  );
};

export const WellbeingMetricModal: React.FC<WellbeingMetricModalProps> = ({
  metricKey,
  metricLabel,
  data,
  comparisonData,
  isOpen,
  onClose
}) => {
  // Get metric scale labels - must be before early return
  const metricScale = useMemo(() => {
    return getMetricScale(metricKey);
  }, [metricKey]);

  // Prepare chart data - must be before early return
  const chartData: ChartDataPoint[] = useMemo(() => {
    return data.daily
      .filter(day => day.metrics[metricKey] !== undefined)
      .map(day => ({
        date: day.date,
        dateLabel: format(parseISO(day.date), 'MMM d'),
        average: day.metrics[metricKey].average,
        count: day.metrics[metricKey].count
      }));
  }, [data.daily, metricKey]);

  if (!isOpen) return null;

  // Find the metric in the data
  const metric = data.overall.find(m => m.key === metricKey);
  const comparisonMetric = comparisonData?.overall.find(m => m.key === metricKey);

  // Calculate trend
  let trend = 0;
  let trendType: 'up' | 'down' | 'stable' = 'stable';
  
  if (comparisonMetric && metric && comparisonMetric.average !== null && metric.average !== null) {
    trend = metric.average - comparisonMetric.average;
    trendType = trend > 0 ? 'up' : trend < 0 ? 'down' : 'stable';
  } else if (metric?.trend !== null && metric?.trend !== undefined) {
    trend = metric.trend;
    trendType = trend > 0 ? 'up' : trend < 0 ? 'down' : 'stable';
  }

  const TrendIcon = trendType === 'up' 
    ? TrendingUp 
    : trendType === 'down' 
      ? TrendingDown 
      : Minus;

  const trendColor = trendType === 'up' 
    ? 'text-green-600' 
    : trendType === 'down' 
      ? 'text-red-600' 
      : 'text-rm-neutral-600';

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-card shadow-card max-w-4xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-rm-neutral-600/10">
            <div>
              <h2 className="text-xl font-semibold text-rm-primary-700">
                {metricLabel}
              </h2>
              <div className="flex items-center gap-3 mt-1">
                <span className="text-2xl font-bold text-rm-ink-900">
                  {metric?.average !== null && metric?.average !== undefined 
                    ? metric.average.toFixed(1) 
                    : 'N/A'}
                </span>
                <span className="text-sm text-rm-neutral-600">/ 10</span>
                {Math.abs(trend) >= 0.01 && (
                  <div className={cn('flex items-center gap-1', trendColor)}>
                    <TrendIcon className="w-4 h-4" />
                    <span className="text-sm font-medium">
                      {trend > 0 ? '+' : ''}{trend.toFixed(1)} pts
                    </span>
                  </div>
                )}
              </div>
              <p className="text-sm text-rm-neutral-600 mt-1">
                {data.summary.uniqueUsers} {data.summary.uniqueUsers === 1 ? 'user' : 'users'} · {metric?.count || 0} entries
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-rm-bg-50 text-rm-neutral-600 hover:text-rm-ink-900 transition-colors self-start ml-auto"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-5 overflow-y-auto max-h-[calc(90vh-120px)]">
            {chartData.length > 0 ? (
              <div>
                <h3 className="text-sm font-semibold text-rm-ink-900 mb-4">
                  Daily Trend ({format(parseISO(data.summary.dateRange.startDate), 'MMM d')} – {format(parseISO(data.summary.dateRange.endDate), 'MMM d, yyyy')})
                </h3>
                {/* Top Label */}
                <div className="text-xs text-rm-neutral-600 text-left mb-1">
                  {metricScale.highLabel}
                </div>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart
                    data={chartData}
                    margin={{
                      top: 5,
                      right: 30,
                      left: 20,
                      bottom: 5,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis 
                      dataKey="dateLabel" 
                      stroke="#6b7280"
                      fontSize={12}
                      tickLine={false}
                    />
                    <YAxis 
                      stroke="#6b7280"
                      fontSize={12}
                      tickLine={false}
                      domain={[1, 10]}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    
                    {/* Reference lines for scale context */}
                    <ReferenceLine y={5} stroke="#d1d5db" strokeDasharray="2 2" />
                    <ReferenceLine y={7.5} stroke="#d1d5db" strokeDasharray="2 2" />
                    
                    <Line
                      type="monotone"
                      dataKey="average"
                      name={metricLabel}
                      stroke={getMetricColor(metricKey)}
                      strokeWidth={3}
                      dot={{ 
                        fill: getMetricColor(metricKey),
                        strokeWidth: 2,
                        r: 5
                      }}
                      connectNulls={false}
                      activeDot={{ r: 7, stroke: getMetricColor(metricKey), strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
                {/* Bottom Label */}
                <div className="text-xs text-rm-neutral-600 text-left mt-1">
                  {metricScale.lowLabel}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg">
                <p className="text-gray-500">No data available for this metric</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WellbeingMetricModal;
